import * as THREE from 'three';
import { createPlasticMaterial, createSampleMaterial, getSampleColor } from '../utils/materials';

export interface SampleData {
  id: string;
  type: string;
  donor: string;
  date: string;
  position: [number, number];
  volume: number;
  unit: string;
}

export interface BoxData {
  id: string;
  label: string;
  row: number;
  col: number;
  samples: SampleData[];
}

const GRID_SIZE = 3;
const TUBE_RADIUS = 0.015;
const TUBE_HEIGHT = 0.06;
const BOX_SIZE = 0.18;
const BOX_HEIGHT = 0.08;

export class SampleBox {
  public mesh: THREE.Group;
  public boxData: BoxData;
  public sampleMeshes: Map<string, THREE.Mesh> = new Map();
  private highlightedSamples: Set<string> = new Set();

  constructor(data: BoxData) {
    this.boxData = data;
    this.mesh = new THREE.Group();
    this.mesh.userData = { type: 'box', id: data.id, boxRef: this };
    this.buildBox();
    this.buildSamples();
  }

  private buildBox(): void {
    // Box container
    const boxGeo = new THREE.BoxGeometry(BOX_SIZE, BOX_HEIGHT, BOX_SIZE);
    const boxMat = createPlasticMaterial(0xd0dae4);
    const box = new THREE.Mesh(boxGeo, boxMat);
    box.position.y = BOX_HEIGHT / 2;
    box.castShadow = true;
    box.receiveShadow = true;
    this.mesh.add(box);

    // Grid dividers
    const dividerMat = createPlasticMaterial(0xbcc8d4);
    for (let i = 1; i < GRID_SIZE; i++) {
      const t = (i / GRID_SIZE) * BOX_SIZE - BOX_SIZE / 2;
      // X dividers
      const divX = new THREE.Mesh(
        new THREE.BoxGeometry(0.002, BOX_HEIGHT * 0.8, BOX_SIZE * 0.95),
        dividerMat
      );
      divX.position.set(t, BOX_HEIGHT / 2, 0);
      this.mesh.add(divX);
      // Z dividers
      const divZ = new THREE.Mesh(
        new THREE.BoxGeometry(BOX_SIZE * 0.95, BOX_HEIGHT * 0.8, 0.002),
        dividerMat
      );
      divZ.position.set(0, BOX_HEIGHT / 2, t);
      this.mesh.add(divZ);
    }
  }

  private buildSamples(): void {
    const cellSize = BOX_SIZE / GRID_SIZE;

    for (const sample of this.boxData.samples) {
      const [row, col] = sample.position;
      const color = getSampleColor(sample.type);

      // Sample tube (cylinder)
      const tubeGeo = new THREE.CylinderGeometry(TUBE_RADIUS, TUBE_RADIUS, TUBE_HEIGHT, 8);
      const tubeMat = createSampleMaterial(color);
      const tube = new THREE.Mesh(tubeGeo, tubeMat);

      const x = (col - (GRID_SIZE - 1) / 2) * cellSize;
      const z = (row - (GRID_SIZE - 1) / 2) * cellSize;
      tube.position.set(x, BOX_HEIGHT + TUBE_HEIGHT / 2, z);
      tube.castShadow = true;

      // Cap
      const capGeo = new THREE.CylinderGeometry(TUBE_RADIUS + 0.002, TUBE_RADIUS + 0.002, 0.008, 8);
      const capMat = createPlasticMaterial(new THREE.Color(color).multiplyScalar(0.7).getHex());
      const cap = new THREE.Mesh(capGeo, capMat);
      cap.position.y = TUBE_HEIGHT / 2 + 0.004;
      tube.add(cap);

      tube.userData = { type: 'sample', id: sample.id, sampleData: sample };
      this.mesh.add(tube);
      this.sampleMeshes.set(sample.id, tube);
    }
  }

  highlightSample(sampleId: string): void {
    this.highlightedSamples.add(sampleId);
    const mesh = this.sampleMeshes.get(sampleId);
    if (mesh) {
      if (!mesh.userData.originalMaterial) {
        mesh.userData.originalMaterial = mesh.material;
      }
      // Élénk zöld kiemelés
      mesh.material = new THREE.MeshBasicMaterial({
        color: 0x00ff66,
        transparent: true,
        opacity: 1.0,
      });
    }
  }

  unhighlightSample(sampleId: string): void {
    this.highlightedSamples.delete(sampleId);
    const mesh = this.sampleMeshes.get(sampleId);
    if (mesh && mesh.userData.originalMaterial) {
      mesh.material = mesh.userData.originalMaterial;
      mesh.userData.originalMaterial = null;
    }
  }

  unhighlightAll(): void {
    for (const id of [...this.highlightedSamples]) {
      this.unhighlightSample(id);
    }
  }

  updatePulse(time: number): void {
    for (const sampleId of this.highlightedSamples) {
      const mesh = this.sampleMeshes.get(sampleId);
      if (mesh) {
        // Méret pulzálás
        const scale = 1.0 + 0.5 * Math.sin(time * 4);
        mesh.scale.set(scale, 1.0 + 0.3 * Math.sin(time * 4), scale);
        // Szín pulzálás zöld és fehér között
        const mat = mesh.material as THREE.MeshBasicMaterial;
        const t = (Math.sin(time * 4) + 1) / 2;
        mat.color.setRGB(t * 0.3, 0.8 + t * 0.2, t * 0.4);
      }
    }
  }
}
